import { ExternalLink, PackageCheck } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { Pill } from '../ui/Pill';
import { formatDateTime } from '../../lib/dateFmt';
import type { IssuedItem } from './meTypes';

/**
 * Everything already issued and signed for, grouped by station, with a
 * link to each captured signature.
 */
export function IssuedItemsCard({ issued }: { issued: IssuedItem[] }) {
  const grouped = groupByStation(issued);
  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <PackageCheck className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-fg">Items issued &amp; signed</h2>
      </div>
      {issued.length === 0 ? (
        <EmptyState title="Nothing issued yet" />
      ) : (
        <div className="divide-y divide-divider/10">
          {Object.entries(grouped).map(([station, rows]) => (
            <div key={station} className="px-5 py-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-accent">
                {station}
              </div>
              <ul className="mt-2 space-y-2">
                {rows.map((it, i) => (
                  <li
                    key={`${it.event_id}-${i}`}
                    className="flex items-baseline justify-between gap-3 text-sm text-fg"
                  >
                    <span>
                      {it.category ? (
                        <span className="mr-1.5">
                          <Pill tone="warning" size="sm">
                            {it.category}
                          </Pill>
                        </span>
                      ) : null}
                      {it.outcome === 'confirmed' ? (
                        <span className="mr-1.5">
                          <Pill tone="success" size="sm">
                            Confirmed
                          </Pill>
                        </span>
                      ) : null}
                      {it.outcome === 'not_present' ? (
                        <span className="mr-1.5">
                          <Pill tone="danger" size="sm">
                            Missing
                          </Pill>
                        </span>
                      ) : null}
                      <span className="font-medium">{it.name}</span>
                      {it.quantity > 1 ? (
                        <span className="text-fg-muted"> × {it.quantity}</span>
                      ) : null}
                      {it.size ? (
                        <span className="text-fg-muted"> · size {it.size}</span>
                      ) : null}
                      {it.serial ? (
                        <span className="text-fg-muted"> · #{it.serial}</span>
                      ) : null}
                      {it.registration ? (
                        <span className="text-fg-muted"> · {it.registration}</span>
                      ) : null}
                      {it.last_service_date ? (
                        <span className="text-fg-muted"> · last serviced {it.last_service_date}</span>
                      ) : null}
                      <span className="ml-2 text-xs text-fg-dim">
                        {formatDateTime(it.issued_at)}
                      </span>
                    </span>
                    {it.signature_key ? (
                      <a
                        href={`/api/signatures/${it.signature_key}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent underline-offset-2 hover:underline"
                      >
                        View signature
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function groupByStation(items: IssuedItem[]): Record<string, IssuedItem[]> {
  const groups: Record<string, IssuedItem[]> = {};
  for (const it of items) {
    if (!groups[it.station]) groups[it.station] = [];
    groups[it.station]!.push(it);
  }
  return groups;
}
